import React, { useState, useEffect } from 'react';
import { supabase, recreateSupabaseClient } from '../lib/api/supabase';
import { useLanguage } from '../contexts/LanguageContext';
import { TAILWIND_COLORS, COLOR_PALETTE } from '../utils/constants';
import { useAuth } from './AuthProvider';
import { usePushNotifications } from '../hooks/usePushNotifications';

// Helper functions from App.js
const safeCoerceNumber = (val) => {
    if (val === null || val === undefined || val === '') return 0;
    const num = Number(String(val).replace(/[^0-9.-]+/g, ''));
    return isNaN(num) ? 0 : num;
};

const getProductCost = (product, getProductCostProp) => {
    if (!product) return 0;
    // Use the passed function if available, otherwise fallback
    if (getProductCostProp) {
        return getProductCostProp(product);
    }
    // For now, return the costproduct if available, otherwise 0
    return safeCoerceNumber(product.costproduct || 0);
};

// Define simple components inline
const Button = ({ children, onClick, color, className = '', type = 'button', disabled = false }) => {
    const pColor = color || 'indigo'; // Default will be handled by the caller or index.css
    const colorClasses = `bg-${pColor}-600 hover:bg-${pColor}-700 focus:ring-${pColor}-500 text-white`;
    return (
        <button type={type} onClick={onClick} disabled={disabled}
            className={`inline-flex items-center justify-center gap-2 px-4 py-2 text-sm font-semibold rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-offset-2 transition-all disabled:opacity-50 disabled:cursor-not-allowed ${colorClasses} ${className}`}>
            {children}
        </button>
    );
};

const Card = ({ children, className }) => (
    <div className={`bg-white rounded-lg shadow-md p-4 mb-4 ${className}`}>{children}</div>
);

const Badge = ({ children, color, className }) => (
    <span className={`px-2 py-1 text-xs font-medium rounded-full ${color} ${className}`}>{children}</span>
);

const SectionHeader = ({ title, icon, color }) => (
    <div className="flex items-center gap-3 mb-6 mt-12 first:mt-0 pt-6 first:pt-0">
        <div className={`p-2.5 rounded-xl bg-${color}-50 text-${color}-600 shadow-sm border border-${color}-100`}>
            {icon}
        </div>
        <h2 className="text-2xl font-bold text-gray-800 tracking-tight">{title}</h2>
    </div>
);

const IconButton = ({ icon, onClick, color = 'gray', tooltip, size = 'md', className = '' }) => {
    const sizeClasses = size === 'sm' ? 'p-1' : 'p-2';
    const colorClasses = {
        gray: 'text-gray-400 hover:text-gray-600 hover:bg-gray-100',
        red: 'text-red-400 hover:text-red-600 hover:bg-red-50',
        blue: 'text-blue-400 hover:text-blue-600 hover:bg-blue-50',
        green: 'text-green-400 hover:text-green-600 hover:bg-green-50',
        indigo: 'text-indigo-400 hover:text-indigo-600 hover:bg-indigo-50',
        white: 'text-white hover:bg-white/20',
    }[color] || 'text-gray-400 hover:text-gray-600 hover:bg-gray-100';

    return (
        <button
            onClick={onClick}
            title={tooltip}
            className={`${sizeClasses} ${colorClasses} rounded-full transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-offset-1 focus:ring-gray-300 ${className}`}
        >
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className={size === 'sm' ? "w-4 h-4" : "w-5 h-5"}>
                <path d={icon} />
            </svg>
        </button>
    );
};



const ICONS_MAP = {
    add: "M12 4.5a.75.75 0 01.75.75v6h6a.75.75 0 010 1.5h-6v6a.75.75 0 01-1.5 0v-6h-6a.75.75 0 010-1.5h6v-6A.75.75 0 0112 4.5z",
    trash: "M14.74 9l-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 01-2.244 2.077H8.084a2.25 2.25 0 01-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 00-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 013.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 00-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 00-7.5 0",
    save: "M16.5 6V4.5A1.5 1.5 0 0015 3H4.5A1.5 1.5 0 003 4.5v15A1.5 1.5 0 004.5 21h10.5A1.5 1.5 0 0016.5 19.5V18",
    edit: "M16.862 4.487l1.687-1.688a1.875 1.875 0 112.652 2.652L10.582 16.07a4.5 4.5 0 01-1.897 1.13L6 18l.8-2.685a4.5 4.5 0 011.13-1.897l8.932-8.931zm0 0L19.5 7.125M18 14v4.75A2.25 2.25 0 0115.75 21H5.25A2.25 2.25 0 013 18.75V8.25A2.25 2.25 0 015.25 6H10",
    close: "M6 18L18 6M6 6l12 12",
    upload: "M3 17a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zM6.293 6.707a1 1 0 010-1.414l3-3a1 1 0 011.414 0l3 3a1 1 0 01-1.414 1.414L11 5.414V13a1 1 0 11-2 0V5.414L7.707 6.707a1 1 0 01-1.414 0z",
    download: "M3 17a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zM6.293 6.707a1 1 0 010-1.414l3-3a1 1 0 011.414 0l3 3a1 1 0 01-1.414 1.414L11 5.414V13a1 1 0 11-2 0V5.414L7.707 6.707a1 1 0 01-1.414 0z",
    store: "M13.5 21v-7.5a.75.75 0 01.75-.75h3a.75.75 0 01.75.75V21m-16.5 0h19.5m-19.5 0v-10.5c0-1.243 1.007-2.25 2.25-2.25h15c1.243 0 2.25 1.007 2.25 2.25V21m-16.5 0h3.375a1.125 1.125 0 011.125-1.125h3.375a1.125 1.125 0 011.125 1.125H16.5M2.25 9.75L12 3l9.75 6.75",
    appearance: "M9.53 16.122a3 3 0 00-5.78 1.128 2.25 2.25 0 01-2.4 2.245 4.5 4.5 0 008.4-2.245c0-.399-.078-.78-.22-1.128zm0 0a15.998 15.998 0 003.388-1.62m-5.043-.025a15.994 15.994 0 011.622-3.395m3.42 3.42a15.995 15.995 0 004.764-4.648l3.876-5.835a1.156 1.156 0 00-1.59-1.591L14.128 6.43a15.99 15.99 0 00-4.647 4.764m3.42 3.42l-.248.248a4.5 4.5 0 01-6.364-6.364l.248-.248m6.364 6.364c.23.23.444.47.64.722l.462.593a1.125 1.125 0 001.325.275l.153-.07a1.147 1.147 0 011.397.107l.39.39a1.125 1.125 0 001.592 0l.54-.54a1.125 1.125 0 000-1.592l-.39-.39a1.147 1.147 0 01-.107-1.397l.07-.153a1.125 1.125 0 00-.275-1.325l-.593-.462a1.496 1.496 0 01-.722-.64",
    invoice: "M19.5 14.25v-2.625a3.375 3.375 0 00-3.375-3.375h-1.5A1.125 1.125 0 0113.5 7.125v-1.5a3.375 3.375 0 00-3.375-3.375H8.25m0 12.75h7.5m-7.5 3H12M10.5 2.25H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 00-9-9z",
    inventory: "M20.25 7.5l-.625 10.125a3.75 3.75 0 01-3.75 3.75H8.125a3.75 3.75 0 01-3.75-3.75L3.75 7.5M10 3h4m-7 4.5h10a1 1 0 001-1V5a1 1 0 00-1-1h-10a1 1 0 00-1 1v1.5a1 1 0 001 1z",
    database: "M20.25 6.375c0 2.278-3.694 4.125-8.25 4.125S3.75 8.653 3.75 6.375m16.5 0c0-2.278-3.694-4.125-8.25-4.125S3.75 4.097 3.75 6.375m16.5 0v11.25c0 2.278-3.694 4.125-8.25 4.125s-8.25-1.847-8.25-4.125V6.375m16.5 0v3.75m-16.5-3.75v3.75m16.5 0v3.75C20.25 16.128 16.556 17.975 12 17.975s-8.25-1.847-8.25-4.125v-3.75m16.5 0v3.75",
    users: "M15 19.128a9.38 9.38 0 002.625.372 9.337 9.337 0 004.121-.952 4.125 4.125 0 00-7.533-2.493M15 19.128v-.003c0-1.113-.285-2.16-.786-3.07M15 19.128v.106A12.318 12.318 0 018.624 21c-2.331 0-4.512-.645-6.374-1.766l-.001-.109a6.375 6.375 0 0111.964-3.07M12 6.375a3.375 3.375 0 11-6.75 0 3.375 3.375 0 016.75 0zm8.25 2.25a2.625 2.625 0 11-5.25 0 2.625 2.625 0 015.25 0z",
};



// Utility to export JSON data
const exportJson = (data, filename) => {
    const jsonStr = JSON.stringify(data, null, 2);
    const blob = new Blob([jsonStr], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${filename}_${new Date().toISOString().slice(0, 10)}.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
};

// Utility to export simple data to CSV/Excel format
const exportCsv = (data, filename) => {
    if (data.length === 0) return;

    // Use Papa.unparse for reliable CSV creation
    const Papa = window.Papa;
    if (!Papa) return console.error("PapaParse library not loaded.");

    // Add UTF-8 BOM for Excel compatibility and Arabic character support
    const csv = '\ufeff' + Papa.unparse(data, {
        delimiter: ',', // Consistent comma delimiter
        quotes: true, // Quote fields containing delimiters or special chars
        quoteChar: '"',
        escapeChar: '"',
        header: true, // Assume data has headers
        newline: '\r\n' // Windows-style newlines for better Excel compatibility
    });

    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${filename}_${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
};

export default function SettingsView({ data, setData, showAlert, getProductCost: getProductCostProp, syncSettings, syncMultipleItems, syncObjectData }) {
    const { t } = useLanguage();
    const { user } = useAuth();
    const { 
        isSupported,
        permission,
        isSubscribed,
        isLoading: isPushLoading,
        error: pushError,
        subscribe: subscribeToPush,
        unsubscribe: unsubscribeFromPush,
        sendTestNotification
    } = usePushNotifications(user?.id);
    
    const FOCUSED_INPUT_STYLE = `border-gray-400 focus:ring-2 focus:ring-offset-1 focus:ring-${data?.settings?.primaryColor || 'indigo'}-500 focus:border-${data?.settings?.primaryColor || 'indigo'}-500`;

    const IconButton = ({ icon, onClick, color, className, tooltip }) => {
        // Map color names to explicit Tailwind classes (Tailwind doesn't support dynamic class names)
        const colorMap = {
            'red': 'text-red-600 hover:text-red-700',
            'blue': 'text-blue-600 hover:text-blue-700',
            'green': 'text-green-600 hover:text-green-700',
            'yellow': 'text-yellow-600 hover:text-yellow-700',
            'purple': 'text-purple-600 hover:text-purple-700',
            'indigo': `text-${data?.settings?.primaryColor || 'indigo'}-600 hover:text-${data?.settings?.primaryColor || 'indigo'}-700`,
            'gray': 'text-gray-600 hover:text-gray-700',
            'orange': 'text-orange-600 hover:text-orange-700',
        };
        const colorClasses = colorMap[color] || 'text-gray-600 hover:text-gray-700';

        return (
            <button onClick={onClick} className={`p-2 rounded-full hover:bg-gray-200 transition-colors relative group ${className}`}>
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className={`w-5 h-5 ${colorClasses}`}>
                    <path d={icon} />
                </svg>
                {tooltip && <span className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 hidden group-hover:block w-max bg-gray-700 text-white text-xs rounded py-1 px-2 z-10">{tooltip}</span>}
            </button>
        );
    };

    const [users, setUsers] = useState([]);
    const [loadingUsers, setLoadingUsers] = useState(true);
    const [editingRole, setEditingRole] = useState(null);
    const [newUserEmail, setNewUserEmail] = useState('');
    const [newUserPassword, setNewUserPassword] = useState('');
    const [addingUser, setAddingUser] = useState(false);
    const [showSupabaseModal, setShowSupabaseModal] = useState(false);
    const [showExportMenu, setShowExportMenu] = useState(false);
    const [showImportMenu, setShowImportMenu] = useState(false);
    const [showStructureModal, setShowStructureModal] = useState(false);
    const [showWooCommerceModal, setShowWooCommerceModal] = useState(false);
    const [showInvoiceSettingsModal, setShowInvoiceSettingsModal] = useState(false);
    const [savingSettings, setSavingSettings] = useState(false);
    const [renamingCategory, setRenamingCategory] = useState({ type: null, oldName: '', currentName: '' });

    // Supabase config state
    const [supabaseUrl, setSupabaseUrl] = useState(() => {
        const settings = localStorage.getItem('lilac_supabase_settings');
        return settings ? JSON.parse(settings).url || process.env.REACT_APP_SUPABASE_URL || '' : process.env.REACT_APP_SUPABASE_URL || '';
    });
    const [supabaseKey, setSupabaseKey] = useState(() => {
        const settings = localStorage.getItem('lilac_supabase_settings');
        return settings ? JSON.parse(settings).key || process.env.REACT_APP_SUPABASE_ANON_KEY || '' : process.env.REACT_APP_SUPABASE_ANON_KEY || '';
    });
    const [showKey, setShowKey] = useState(false);

    // WooCommerce Mapping state
    const [showWooMappingModal, setShowWooMappingModal] = useState(false);
    const [wooMappings, setWooMappings] = useState([]);
    const [newWooMappingName, setNewWooMappingName] = useState('');
    const [newWooMappingCrmItem, setNewWooMappingCrmItem] = useState('');

    useEffect(() => {
        fetchUsers();

        // Add click listener to close dropdowns when clicking outside
        const handleClickOutside = (event) => {
            if (!event.target.closest('.dropdown-container')) {
                setShowExportMenu(false);
                setShowImportMenu(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const fetchWooMappings = async () => {
        try {
            const { data: mappings, error } = await supabase.from('woo_product_map').select('*');
            if (error) throw error;
            setWooMappings(mappings || []);
        } catch (error) {
            console.error('Error fetching woo mappings:', error);
            showAlert('Error fetching WooCommerce mappings: ' + error.message);
        }
    };

    const fetchUsers = async () => {
        try {
            const { data: profiles, error } = await supabase
                .from('profiles')
                .select('id, email, role, created_at')
                .order('created_at', { ascending: false });

            if (error) throw error;
            setUsers(profiles || []);
        } catch (error) {
            console.error('Error fetching users:', error);
            showAlert('Error fetching users: ' + error.message);
        } finally {
            setLoadingUsers(false);
        }
    };

    const updateUserRole = async (userId, newRole) => {
        try {
            const { error } = await supabase
                .from('profiles')
                .update({ role: newRole })
                .eq('id', userId);

            if (error) throw error;

            setUsers(users.map(u => u.id === userId ? { ...u, role: newRole } : u));
            setEditingRole(null);
            showAlert('User role updated successfully');
        } catch (error) {
            console.error('Error updating role:', error);
            showAlert('Error updating role: ' + error.message);
        }
    };

    const addUser = async () => {
        if (!newUserEmail || !newUserPassword) {
            showAlert('Please provide both email and password');
            return;
        }

        setAddingUser(true);
        try {
            const { data, error } = await supabase.auth.signUp({
                email: newUserEmail,
                password: newUserPassword,
            });

            if (error) throw error;

            // Wait a moment for the profile to be created via trigger
            setTimeout(() => {
                fetchUsers();
                setNewUserEmail('');
                setNewUserPassword('');
                showAlert('User added successfully. They can now log in.');
            }, 1000);
        } catch (error) {
            console.error('Error adding user:', error);
            showAlert('Error adding user: ' + error.message);
        } finally {
            setAddingUser(false);
        }
    };

    const removeUser = async (userId, userEmail) => {
        if (!window.confirm(`Are you sure you want to permanently delete user ${userEmail}? This action cannot be undone.`)) {
            return;
        }

        try {
            // First delete the profile
            const { error: profileError } = await supabase
                .from('profiles')
                .delete()
                .eq('id', userId);

            if (profileError) throw profileError;

            // Then delete the auth user (admin only)
            const { error: authError } = await supabase.auth.admin.deleteUser(userId);
            if (authError) throw authError;

            setUsers(users.filter(u => u.id !== userId));
            showAlert('User deleted successfully');
        } catch (error) {
            console.error('Error deleting user:', error);
            showAlert('Error deleting user: ' + error.message);
        }
    };

    const saveSupabaseConfig = () => {
        if (!supabaseUrl || !supabaseKey) {
            showAlert('Please provide both URL and Key');
            return;
        }

        // Save to localStorage
        localStorage.setItem('lilac_supabase_settings', JSON.stringify({
            url: supabaseUrl,
            key: supabaseKey,
            enabled: true
        }));

        // Recreate the Supabase client with new config
        recreateSupabaseClient({ url: supabaseUrl, anon: supabaseKey });

        // Show success message and close modal
        showAlert('Supabase configuration saved and client updated.');
        setShowSupabaseModal(false);
    };

    const handleWooCsvImport = () => {
        const input = document.createElement('input');
        input.type = 'file';
        input.accept = '.csv';
        input.onchange = async (e) => {
            const file = e.target.files[0];
            if (!file) return;

            const Papa = window.Papa;
            if (!Papa) {
                showAlert("Please wait, data parsing library is still loading.");
                return;
            }

            const reader = new FileReader();
            reader.onload = async (event) => {
                const fileContent = event.target.result;
                Papa.parse(fileContent, {
                    header: true,
                    skipEmptyLines: true,
                    complete: async (results) => {
                        const parsedData = results.data;
                        let newMappingsCount = 0;
                        let existingMappingsCount = 0;
                        let failedMappings = [];

                        // We need to look for columns like "Name" representing the WooCommerce product name
                        // Sometimes WooCommerce exports it as "name" or "Product Name"

                        const crmProductNames = data.products.map(p => p.product.toLowerCase());
                        const crmProductsMap = {};
                        data.products.forEach(p => {
                            crmProductsMap[p.product.toLowerCase()] = p.product;
                        });

                        const mappingsToInsert = [];

                        parsedData.forEach(row => {
                            // Find the product name column in the CSV
                            const rowKeys = Object.keys(row);
                            const nameKey = rowKeys.find(key => key.toLowerCase().includes('name'));

                            if (nameKey && row[nameKey]) {
                                const wooName = row[nameKey].trim();
                                if (!wooName) return;

                                // Skip if already mapped
                                if (wooMappings.some(m => m.woo_product_name === wooName)) {
                                    existingMappingsCount++;
                                    return;
                                }

                                // Try to auto-match based on exact name or very close name
                                const searchName = wooName.toLowerCase();

                                // Auto mapping Strategy 1: Exact Match
                                let mappedCrmName = crmProductsMap[searchName];

                                // Auto mapping Strategy 2: Removing WooCommerce variable fluff like " - Size: 50ml, Gender: For Him"
                                if (!mappedCrmName) {
                                    // simple heuristic: everything before the first hyphen might be the base name
                                    const baseNameMatch = searchName.split('-')[0].trim();

                                    // Is there a CRM product that contains this base name?
                                    // E.g. woo: "Lavender - Size 50ml" -> base: "lavender" -> CRM match: "Lavender 50ml"
                                    const possibleMatches = data.products.filter(p =>
                                        p.product.toLowerCase().includes(baseNameMatch)
                                    );

                                    // If only one logical match exists, use it
                                    if (possibleMatches.length === 1) {
                                        mappedCrmName = possibleMatches[0].product;
                                    }
                                }

                                if (mappedCrmName) {
                                    mappingsToInsert.push({
                                        woo_product_name: wooName,
                                        crm_product_name: mappedCrmName
                                    });
                                    newMappingsCount++;
                                } else {
                                    failedMappings.push(wooName);
                                }
                            }
                        });

                        if (mappingsToInsert.length > 0) {
                            try {
                                const { error } = await supabase.from('woo_product_map').upsert(
                                    mappingsToInsert,
                                    { onConflict: 'woo_product_name' }
                                );
                                if (error) throw error;

                                await fetchWooMappings();
                                showAlert(`Successfully auto-mapped ${newMappingsCount} products! (${existingMappingsCount} already existed). ${failedMappings.length} products could not be auto-mapped.`);
                            } catch (error) {
                                console.error('Error in bulk mapping:', error);
                                showAlert('Failed to save bulk mappings: ' + error.message);
                            }
                        } else {
                            if (failedMappings.length > 0) {
                                showAlert(`Could not auto-map any of the ${failedMappings.length} products. They require manual mapping.`);
                            } else {
                                showAlert(`No new products to map. ${existingMappingsCount} products were already mapped.`);
                            }
                        }
                    }
                });
            };
            reader.readAsText(file);
        };
        input.click();
    };

    const handleSaveWooMapping = async () => {
        if (!newWooMappingName || !newWooMappingCrmItem) {
            showAlert('Please select both a WooCommerce product name and a CRM product');
            return;
        }

        try {
            const { error } = await supabase.from('woo_product_map').upsert(
                { woo_product_name: newWooMappingName, crm_product_name: newWooMappingCrmItem },
                { onConflict: 'woo_product_name' }
            );
            if (error) throw error;

            await fetchWooMappings();
            setNewWooMappingName('');
            setNewWooMappingCrmItem('');
            showAlert('Mapping saved successfully!');
        } catch (error) {
            console.error('Error saving mapping:', error);
            showAlert('Failed to save mapping');
        }
    };

    const handleDeleteWooMapping = async (wooName) => {
        if (!window.confirm(`Delete mapping for "${wooName}"?`)) return;
        try {
            const { error } = await supabase.from('woo_product_map').delete().eq('woo_product_name', wooName);
            if (error) throw error;
            await fetchWooMappings();
            showAlert('Mapping deleted');
        } catch (error) {
            console.error('Error deleting mapping:', error);
            showAlert('Failed to delete mapping');
        }
    };

    const handleFileImport = (targetKey) => {
        const input = document.createElement('input');
        input.type = 'file';
        input.accept = targetKey === 'all' ? '.json' : '.csv,.xlsx,.json';
        input.onchange = async (e) => {
            const file = e.target.files[0];
            if (!file) return;

            const reader = new FileReader();
            reader.onload = async (event) => {
                try {
                    const fileContent = event.target.result;
                    const fileExtension = file.name.split('.').pop().toLowerCase();

                    if (targetKey === 'all') {
                        // Import all data from JSON
                        if (fileExtension !== 'json') {
                            showAlert('Please select a JSON file for importing all data');
                            return;
                        }
                        const allData = JSON.parse(fileContent);
                        setData(currentData => {
                            let newData = JSON.parse(JSON.stringify(currentData));
                            if (allData.customers) newData.customers = allData.customers;
                            if (allData.products) newData.products = allData.products;
                            if (allData.orders) newData.orders = allData.orders;
                            if (allData.bottles) newData.bottles = allData.bottles;
                            if (allData.oils) newData.oils = allData.oils;
                            if (allData.shipping) newData.shipping = allData.shipping;
                            if (allData.settings) newData.settings = allData.settings;
                            if (allData.generalSettings) newData.generalSettings = allData.generalSettings;
                            if (allData.adsLedger) newData.adsLedger = allData.adsLedger;
                            if (allData.stockSettings) newData.stockSettings = allData.stockSettings;
                            if (allData.returnedPool) newData.returnedPool = allData.returnedPool;
                            return newData;
                        });

                        // Sync all data to Supabase
                        if (syncMultipleItems && syncObjectData) {
                            try {
                                console.log('🔄 Syncing all imported data to Supabase...');
                                const syncPromises = [];
                                if (allData.customers) syncPromises.push(syncMultipleItems('customers', allData.customers));
                                if (allData.products) syncPromises.push(syncMultipleItems('products', allData.products));
                                if (allData.orders) syncPromises.push(syncMultipleItems('orders', allData.orders));
                                if (allData.bottles) syncPromises.push(syncMultipleItems('bottles', allData.bottles));
                                if (allData.oils) syncPromises.push(syncMultipleItems('oils', allData.oils));
                                if (allData.shipping) syncPromises.push(syncMultipleItems('shipping', allData.shipping));
                                if (allData.returnedPool) syncPromises.push(syncObjectData('returned_pool', allData.returnedPool, 'product_name', 'quantity'));

                                await Promise.all(syncPromises);
                                console.log('✅ All data synced to Supabase');
                                showAlert('Successfully imported and synced all data.');
                            } catch (err) {
                                console.error('❌ Error syncing imported data:', err);
                                showAlert(`Data imported locally but failed to sync to database: ${err.message}`);
                            }
                        } else {
                            showAlert('Successfully imported all data.');
                        }
                    } else {
                        // Import specific data type
                        if (fileExtension === 'json') {
                            // Handle JSON import for specific data type
                            const jsonData = JSON.parse(fileContent);
                            setData(currentData => {
                                let newData = JSON.parse(JSON.stringify(currentData));
                                newData[targetKey] = jsonData;
                                return newData;
                            });

                            // Sync to Supabase
                            if (syncMultipleItems) {
                                try {
                                    console.log(`🔄 Syncing imported ${targetKey} to Supabase...`);
                                    await syncMultipleItems(targetKey, jsonData);
                                    console.log(`✅ ${targetKey} synced to Supabase`);
                                    showAlert(`Successfully imported and synced ${targetKey} data from JSON.`);
                                } catch (err) {
                                    console.error(`❌ Error syncing ${targetKey}:`, err);
                                    showAlert(`Data imported locally but failed to sync to database: ${err.message}`);
                                }
                            } else {
                                showAlert(`Successfully imported ${targetKey} data from JSON.`);
                            }
                        } else {
                            // Handle CSV import for specific data type
                            const Papa = window.Papa;
                            if (!Papa) {
                                showAlert("Please wait, data parsing library is still loading.");
                                return;
                            }

                            Papa.parse(fileContent, {
                                header: true,
                                skipEmptyLines: true,
                                transformHeader: header => header.toLowerCase().replace(/\s+/g, '').replace(/[^a-z0-9]/gi, ''),
                                complete: async (results) => {
                                    const parsedData = results.data;
                                    let processedData = [];

                                    setData(currentData => {
                                        let newData = JSON.parse(JSON.stringify(currentData));
                                        processedData = parsedData.map((row, index) => {
                                            let newRow = { ...row, id: row.id || `${targetKey.slice(0, 1)}${Date.now() + index}` };
                                            if (targetKey === 'customers') {
                                                newRow.orders = safeCoerceNumber(newRow.orders || 0);
                                                newRow.cancels = newRow.cancels ? String(newRow.cancels).split(',').map(d => d.trim()).filter(Boolean) : [];
                                                newRow.completed = safeCoerceNumber(newRow.completed || 0);
                                            }
                                            if (targetKey === 'shipping') { newRow.fees = safeCoerceNumber(newRow.fees || newRow['fees(£)'] || newRow['']); }
                                            if (targetKey === 'products') {
                                                newRow.qtysold = safeCoerceNumber(newRow.qtysold || 0);
                                                newRow.costproduct = getProductCost(newRow, getProductCostProp);
                                            }
                                            if (targetKey === 'bottles') {
                                                newRow.actualstock = safeCoerceNumber(newRow.actualstock || 0);
                                                newRow.totalordered = safeCoerceNumber(newRow.totalordered || 0);
                                                newRow.totalcostbottle = safeCoerceNumber(newRow.totalcostbottle || 0);
                                            }
                                            if (targetKey === 'oils') {
                                                newRow.actualstock = safeCoerceNumber(newRow.actualstock || 0);
                                                newRow.totalordered = safeCoerceNumber(newRow.totalordered || 0);
                                                newRow.price = safeCoerceNumber(newRow.price || 0);
                                            }
                                            if (targetKey === 'orders') {
                                                if (newRow.itemsjson) {
                                                    try {
                                                        const itemsJson = JSON.parse(newRow.itemsjson);
                                                        newRow.items = itemsJson.map(itemStr => {
                                                            const match = itemStr.match(/^(.+?)\s*x\s*(\d+)$/);
                                                            if (match) {
                                                                return {
                                                                    pid: match[1].trim(),
                                                                    qty: parseInt(match[2]),
                                                                    used: itemStr.toLowerCase().includes('used') || (newRow.package || '').toLowerCase().includes('used')
                                                                };
                                                            } else {
                                                                return {
                                                                    pid: itemStr.trim(),
                                                                    qty: 1,
                                                                    used: (newRow.package || '').toLowerCase().includes('used')
                                                                };
                                                            }
                                                        });
                                                    } catch (e) {
                                                        const productName = String(newRow.product || '').trim();
                                                        const totalQty = safeCoerceNumber(newRow.quantity);
                                                        newRow.items = [{ pid: productName, qty: totalQty, used: (newRow.package || '').toLowerCase().includes('used') }];
                                                    }
                                                } else {
                                                    const productName = String(newRow.product || '').trim();
                                                    const totalQty = safeCoerceNumber(newRow.quantity);
                                                    newRow.items = [{ pid: productName, qty: totalQty, used: (newRow.package || '').toLowerCase().includes('used') }];
                                                }
                                            }
                                            return newRow;
                                        });
                                        newData[targetKey] = processedData;
                                        return newData;
                                    });

                                    // Sync to Supabase
                                    if (syncMultipleItems && processedData.length > 0) {
                                        try {
                                            console.log(`🔄 Syncing imported ${targetKey} to Supabase...`);
                                            await syncMultipleItems(targetKey, processedData);
                                            console.log(`✅ ${targetKey} synced to Supabase`);
                                            showAlert(`Successfully imported and synced ${targetKey} data from CSV.`);
                                        } catch (err) {
                                            console.error(`❌ Error syncing ${targetKey}:`, err);
                                            showAlert(`Data imported locally but failed to sync to database: ${err.message}`);
                                        }
                                    } else {
                                        showAlert(`Successfully imported ${targetKey} data from CSV.`);
                                    }
                                }
                            });
                        }
                    }
                } catch (error) {
                    showAlert(`Error importing file: ${error.message}`);
                }
            };
            reader.readAsText(file);
        };
        input.click();
    };

    const handleSaveSettings = async () => {
        if (!syncSettings) {
            showAlert('Settings sync function not available');
            return;
        }

        setSavingSettings(true);
        try {
            await syncSettings();
            showAlert('Settings saved successfully!');
        } catch (error) {
            console.error('Error saving settings:', error);
            showAlert('Error saving settings: ' + error.message);
        } finally {
            setSavingSettings(false);
        }
    };

    return (
        <div className="max-w-6xl mx-auto pb-20">
            <div className="flex justify-between items-center mb-10 border-b pb-6">
                <div>
                    <h1 className="text-4xl font-extrabold text-gray-900 tracking-tight">{t('settings')}</h1>
                    <p className="text-gray-500 mt-2">{t('manageStorePreferences') || 'Manage your store preferences and system configuration.'}</p>
                </div>
                <Button onClick={handleSaveSettings} disabled={savingSettings} color="green" className="px-6 py-3 shadow-lg hover:shadow-xl transform transition-all active:scale-95">
                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="w-5 h-5 mr-2">
                        <path d={ICONS_MAP.save} />
                    </svg>
                    {savingSettings ? t('saving') : t('saveSettings')}
                </Button>
            </div>


            {/* Section: Store Identity */}
            <SectionHeader title={t('storeIdentity')} icon={<svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={ICONS_MAP.store} /></svg>} color="indigo" />
            <Card className="space-y-8 p-6 lg:p-8">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                    <div className="space-y-2">
                        <label className="block text-sm font-bold text-gray-700 uppercase tracking-wider">{t('storeName')}</label>
                        <input
                            type="text"
                            value={data.generalSettings.storeName}
                            onChange={e => setData(prev => ({ ...prev, generalSettings: { ...prev.generalSettings, storeName: e.target.value } }))}
                            className={`block w-full p-3 border rounded-xl shadow-sm transition-all ${FOCUSED_INPUT_STYLE}`}
                            placeholder="e.g. Lilac Valley Perfumes"
                        />
                    </div>
                    <div className="space-y-2">
                        <label className="block text-sm font-bold text-gray-700 uppercase tracking-wider">{t('currency')}</label>
                        <select
                            value={data.generalSettings.currency}
                            onChange={e => setData(prev => ({ ...prev, generalSettings: { ...prev.generalSettings, currency: e.target.value } }))}
                            className={`block w-full p-3 border rounded-xl shadow-sm transition-all ${FOCUSED_INPUT_STYLE}`}
                        >
                            <option value="GBP">GBP (£)</option>
                            <option value="USD">USD ($)</option>
                            <option value="EGP">EGP (EGP)</option>
                            <option value="SAR">SAR (SAR)</option>
                            <option value="AED">AED (AED)</option>
                        </select>
                    </div>
                    <div className="space-y-2">
                        <label className="block text-sm font-bold text-gray-700 uppercase tracking-wider">{t('primaryColor')}</label>
                        <div className="flex gap-4 items-center">
                            <select
                                value={data.settings.primaryColor}
                                onChange={e => setData(prev => ({ ...prev, settings: { ...prev.settings, primaryColor: e.target.value } }))}
                                className={`flex-1 p-3 border rounded-xl shadow-sm transition-all ${FOCUSED_INPUT_STYLE}`}
                            >
                                {TAILWIND_COLORS.map(color => (
                                    <option key={color} value={color}>{color.charAt(0).toUpperCase() + color.slice(1)}</option>
                                ))}
                            </select>
                            <div 
                                className="w-12 h-12 rounded-2xl border-4 border-white shadow-lg flex-shrink-0 transition-all transform hover:scale-110"
                                style={{ backgroundColor: COLOR_PALETTE[data.settings.primaryColor] || '#6366f1' }}
                            />
                        </div>
                    </div>
                    <div className="space-y-2">
                        <label className="block text-sm font-bold text-gray-700 uppercase tracking-wider">{t('logoUrl')}</label>
                        <div className="flex gap-3">
                           <input
                            type="text"
                            value={data.settings.logoUrl}
                            onChange={e => setData(prev => ({ ...prev, settings: { ...prev.settings, logoUrl: e.target.value } }))}
                            className={`flex-1 p-3 border rounded-xl shadow-sm transition-all ${FOCUSED_INPUT_STYLE}`}
                            placeholder="https://example.com/logo.png"
                           />
                           {data.settings.logoUrl && (
                               <div className="w-12 h-12 rounded-xl border bg-gray-50 flex items-center justify-center overflow-hidden flex-shrink-0">
                                   <img src={data.settings.logoUrl} alt="Logo" className="max-w-full max-h-full object-contain" onError={e => e.target.style.display='none'} />
                               </div>
                           )}
                        </div>
                    </div>
                </div>
            </Card>

            {/* Section: Appearance */}
            <SectionHeader title={t('appearance')} icon={<svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={ICONS_MAP.appearance} /></svg>} color="pink" />

            <Card className="space-y-6 p-6 lg:p-8">
                <div className="flex items-center justify-between mb-2">
                    <div>
                        <h3 className="text-lg font-bold text-gray-900">{t('metricsVisibility')}</h3>
                        <p className="text-sm text-gray-500">{t('dashboardSettingsDesc') || 'Customize the layout and visibility of your main dashboard metrics.'}</p>
                    </div>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 mt-4">
                    {[
                        { key: 'showLowStock',      label: t('lowStockAlerts'),      desc: t('lowStockAlertsDesc') || 'Shows warnings for items below threshold' },
                        { key: 'showRevenue',        label: t('netRevenue'),          desc: t('netRevenueChartDesc') || 'Overview of accumulated profit' },
                        { key: 'showOrders',         label: t('totalOrders'),         desc: t('totalOrdersChartDesc') || 'Volume of sales over time' },
                        { key: 'showTopProducts',    label: t('topSellingProducts'),  desc: t('topSellingProductsDesc') || 'Breakdown of best performing items' },
                        { key: 'showTopBottles',     label: t('topSellingBottles'),   desc: t('topSellingBottlesDesc') || 'Distribution of bottle size orders' },
                        { key: 'showTopCustomers',   label: t('topCustomers'),         desc: t('topCustomersDesc') || 'Loyalty metric of your best buyers' },
                    ].map(({ key, label, desc }) => {
                        const isOn = data.generalSettings?.[key] !== false;
                        return (
                            <div
                                key={key}
                                className={`flex items-center justify-between p-5 rounded-2xl border-2 transition-all shadow-sm group hover:shadow-md ${isOn ? `bg-${data.settings.primaryColor}-50/30 border-${data.settings.primaryColor}-100` : 'bg-white border-gray-100'}`}
                            >
                                <div className="flex-1 mr-4">
                                    <p className={`text-sm font-bold ${isOn ? 'text-gray-900' : 'text-gray-400'}`}>{label}</p>
                                    <p className="text-[11px] text-gray-400 mt-1 leading-tight">{desc}</p>
                                </div>
                                <button
                                    onClick={() =>
                                        setData(prev => ({
                                            ...prev,
                                            generalSettings: {
                                                ...prev.generalSettings,
                                                [key]: !isOn,
                                            },
                                        }))
                                    }
                                    dir="ltr"
                                    className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-300 ease-in-out focus:outline-none ring-offset-2 focus:ring-2 focus:ring-indigo-500 ${isOn ? `bg-${data.settings.primaryColor}-600` : 'bg-gray-300'}`}
                                >
                                    <span
                                        className={`inline-block h-5 w-5 transform rounded-full bg-white shadow-lg ring-0 transition duration-300 ease-in-out ${isOn ? 'translate-x-5' : 'translate-x-0'}`}
                                    />
                                </button>
                            </div>
                        );
                    })}
                </div>
            </Card>

            {/* Section: Notifications */}
            <SectionHeader title="Notifications" icon={<svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" /></svg>} color="green" />
            <Card className="p-6 lg:p-8">
                <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
                    <div>
                        <h3 className="text-lg font-bold text-gray-900">Push Notifications</h3>
                        <p className="text-sm text-gray-500 mt-1">Receive background alerts for new website orders. (Works best when the CRM is installed as an app to your home screen.)</p>
                        
                        {!isSupported && (
                            <p className="text-xs text-red-500 font-semibold mt-2">⚠️ Your browser or device does not support push notifications.</p>
                        )}
                        {pushError && (
                            <p className="text-xs text-red-500 font-semibold mt-2">Error: {pushError}</p>
                        )}
                    </div>
                    
                    <div className="flex flex-col sm:flex-row items-center gap-3">
                        {isSubscribed && (
                            <button 
                                onClick={sendTestNotification}
                                className="text-sm font-semibold text-green-600 hover:text-green-700 bg-green-50 px-3 py-1.5 rounded-lg border border-green-200 transition-colors"
                            >
                                Send Test Alert
                            </button>
                        )}
                        
                        <button
                            onClick={isSubscribed ? unsubscribeFromPush : subscribeToPush}
                            disabled={!isSupported || isPushLoading}
                            dir="ltr"
                            className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-300 ease-in-out focus:outline-none ring-offset-2 focus:ring-2 focus:ring-${data.settings.primaryColor}-500 ${!isSupported || isPushLoading ? 'opacity-50 cursor-not-allowed' : ''} ${isSubscribed ? `bg-${data.settings.primaryColor}-600` : 'bg-gray-300'}`}
                        >
                            <span className={`inline-block h-5 w-5 transform rounded-full bg-white shadow-lg ring-0 transition duration-300 ease-in-out ${isSubscribed ? 'translate-x-5' : 'translate-x-0'}`} />
                        </button>
                    </div>
                </div>
            </Card>

            {/* Section: Sales & Billing */}
            <SectionHeader title={t('salesAndBilling')} icon={<svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={ICONS_MAP.invoice} /></svg>} color="amber" />
            <Card className="p-6 lg:p-8">
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-6">
                    <div className="flex-1">
                        <h3 className="text-lg font-bold text-gray-900">{t('invoiceSettings')}</h3>
                        <p className="text-sm text-gray-500 mt-1">{t('invoiceSettingsDesc') || 'Configure your business identity and visual style for customer invoices.'}</p>
                    </div>
                    <Button color="amber" onClick={() => setShowInvoiceSettingsModal(true)} className="whitespace-nowrap">
                        <svg className="w-4 h-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={ICONS_MAP.edit} /></svg>
                        {t('openInvoiceSettings')}
                    </Button>
                </div>
            </Card>

            {/* Section: Inventory Control */}
            <SectionHeader title={t('inventoryControl')} icon={<svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={ICONS_MAP.inventory} /></svg>} color="emerald" />
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                <Card className="p-6 lg:p-8 space-y-6">
                    <h3 className="text-lg font-bold text-gray-900">{t('lowStockThresholds')}</h3>
                    <div className="space-y-4">
                        <div>
                            <label className="block text-xs font-bold text-gray-500 uppercase mb-1">{t('lowStockThresholdOils')}</label>
                            <div className="relative">
                                <input
                                    type="number"
                                    value={data.stockSettings.lowStockThresholds?.oils || 50}
                                    onChange={e => setData(prev => ({
                                        ...prev,
                                        stockSettings: {
                                            ...prev.stockSettings,
                                            lowStockThresholds: {
                                                ...prev.stockSettings.lowStockThresholds,
                                                oils: parseInt(e.target.value) || 0
                                            }
                                        }
                                    }))}
                                    className={`block w-full p-3 border rounded-xl ${FOCUSED_INPUT_STYLE}`}
                                    min="0"
                                />
                                <span className="absolute right-3 top-3 text-gray-400 text-sm">ml</span>
                            </div>
                        </div>
                        <div>
                            <label className="block text-xs font-bold text-gray-500 uppercase mb-1">{t('lowStockThresholdBottles')}</label>
                            <div className="relative">
                                <input
                                    type="number"
                                    value={data.stockSettings.lowStockThresholds?.bottles || 10}
                                    onChange={e => setData(prev => ({
                                        ...prev,
                                        stockSettings: {
                                            ...prev.stockSettings,
                                            lowStockThresholds: {
                                                ...prev.stockSettings.lowStockThresholds,
                                                bottles: parseInt(e.target.value) || 0
                                            }
                                        }
                                    }))}
                                    className={`block w-full p-3 border rounded-xl ${FOCUSED_INPUT_STYLE}`}
                                    min="0"
                                />
                                <span className="absolute right-3 top-3 text-gray-400 text-sm">{t('units') || 'units'}</span>
                            </div>
                        </div>
                    </div>
                </Card>

                <Card className="p-6 lg:p-8 space-y-6">
                    <div className="flex items-center justify-between">
                         <h3 className="text-lg font-bold text-gray-900">{t('inventoryLabels')}</h3>
                    </div>
                    <p className="text-sm text-gray-500">{t('inventoryLabelsDesc')}</p>
                    <div className="grid grid-cols-2 gap-4">
                        <div>
                            <label className="block text-xs font-bold text-gray-500 uppercase mb-1">{t('bottlesLabel')}</label>
                            <input
                                type="text"
                                value={data.stockSettings.inventoryLabels?.bottles || 'Bottles'}
                                onChange={e => setData(prev => ({
                                    ...prev,
                                    stockSettings: {
                                        ...prev.stockSettings,
                                        inventoryLabels: {
                                            ...prev.stockSettings.inventoryLabels,
                                            bottles: e.target.value
                                        }
                                    }
                                }))}
                                className={`block w-full p-3 border rounded-xl ${FOCUSED_INPUT_STYLE}`}
                            />
                        </div>
                        <div>
                            <label className="block text-xs font-bold text-gray-500 uppercase mb-1">{t('oilsLabel')}</label>
                            <input
                                type="text"
                                value={data.stockSettings.inventoryLabels?.oils || 'Oils'}
                                onChange={e => setData(prev => ({
                                    ...prev,
                                    stockSettings: {
                                        ...prev.stockSettings,
                                        inventoryLabels: {
                                            ...prev.stockSettings.inventoryLabels,
                                            oils: e.target.value
                                        }
                                    }
                                }))}
                                className={`block w-full p-3 border rounded-xl ${FOCUSED_INPUT_STYLE}`}
                            />
                        </div>
                    </div>
                </Card>

                <Card className="p-6 lg:p-8 lg:col-span-2">
                    <div className="flex items-center justify-between mb-6">
                        <h3 className="text-lg font-bold text-gray-900">{t('orderProcessing')}</h3>
                        <Button onClick={() => setShowStructureModal(true)} color="indigo" size="sm" className="rounded-full px-4">
                             {t('configureStoreStructure')}
                        </Button>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                        <div className="bg-gray-50 rounded-2xl p-6 border border-gray-100">
                             <h4 className="font-bold text-gray-700 mb-3 flex items-center gap-2">
                                <div className="w-1.5 h-6 bg-indigo-500 rounded-full" />
                                {t('bottleCategories')}
                             </h4>
                             <div className="flex flex-wrap gap-2">
                                {Object.keys(data.stockSettings.bottleVariants || {}).map(cat => (
                                    <Badge key={cat} color="bg-indigo-100 text-indigo-700 border border-indigo-200 capitalize" className="px-3 py-1 font-bold">
                                        {cat}
                                    </Badge>
                                ))}
                             </div>
                        </div>
                        <div className="bg-gray-50 rounded-2xl p-6 border border-gray-100">
                             <h4 className="font-bold text-gray-700 mb-3 flex items-center gap-2">
                                <div className="w-1.5 h-6 bg-emerald-500 rounded-full" />
                                {t('oilCategories')}
                             </h4>
                             <div className="flex flex-wrap gap-2">
                                {(data.stockSettings.oilCategories || []).map(cat => (
                                    <Badge key={cat} color="bg-emerald-100 text-emerald-700 border border-emerald-200 capitalize" className="px-3 py-1 font-bold">
                                        {cat}
                                    </Badge>
                                ))}
                             </div>
                        </div>
                    </div>
                </Card>
            </div>

            {/* Section: System & Data */}
            <SectionHeader title={t('systemAndData')} icon={<svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={ICONS_MAP.database} /></svg>} color="blue" />
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                 <Card className="p-6 lg:p-8 space-y-6">
                    <div className="flex items-center justify-between">
                        <h3 className="text-lg font-bold text-gray-900">{t('databaseConnection')}</h3>
                        <Badge color={supabaseUrl && supabaseKey ? 'bg-green-100 text-green-700 border border-green-200' : 'bg-red-100 text-red-700 border border-red-200'}>
                            {supabaseUrl && supabaseKey ? (t('connected') || 'Connected') : (t('disconnected') || 'Disconnected')}
                        </Badge>
                    </div>
                    <p className="text-sm text-gray-500">{t('supabaseDesc') || 'Configure your Supabase backend connection and security keys.'}</p>
                    <Button onClick={() => setShowSupabaseModal(true)} color="indigo" className="w-full">
                        {t('connectSupabase')}
                    </Button>
                 </Card>

                 <Card className="p-6 lg:p-8 space-y-6">
                    <h3 className="text-lg font-bold text-gray-900">{t('wooCommerceIntegration')}</h3>
                    <p className="text-sm text-gray-500">{t('wooDesc') || 'Sync orders and automate product mapping from your WooCommerce store.'}</p>
                    <div className="flex gap-3">
                        <Button onClick={() => setShowWooCommerceModal(true)} color="purple" className="flex-1">
                            {t('wooCommerceIntegration')}
                        </Button>
                        <Button onClick={() => { fetchWooMappings(); setShowWooMappingModal(true); }} color="orange" className="flex-1">
                            {t('manageWooCommerceMap')}
                        </Button>
                    </div>
                 </Card>

                 <Card className="p-6 lg:p-8 lg:col-span-2 space-y-8">
                    <div className="flex items-center justify-between">
                        <h3 className="text-lg font-bold text-gray-900">{t('importExport')}</h3>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-8 dropdown-container">
                        {/* Export */}
                        <div className="relative group">
                            <label className="block text-xs font-bold text-gray-500 uppercase mb-3">{t('exportData')}</label>
                            <Button onClick={() => { setShowExportMenu(!showExportMenu); setShowImportMenu(false); }} color="blue" className="w-full justify-between h-12 px-5 rounded-xl border-2 border-transparent hover:border-blue-200 shadow-sm">
                                <span className="flex items-center gap-3">
                                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="w-5 h-5">
                                        <path d={ICONS_MAP.download} />
                                    </svg>
                                    {t('exportData')}
                                </span>
                                <svg className={`w-4 h-4 transition-transform ${showExportMenu ? 'rotate-180' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" /></svg>
                            </Button>
                            {showExportMenu && (
                                <div className="absolute top-full left-0 mt-3 w-full bg-white border border-gray-200 rounded-2xl shadow-2xl z-20 overflow-hidden ring-1 ring-black ring-opacity-5">
                                    <div className="py-2">
                                        <button onClick={() => { exportJson(data, 'lilac-valley-data'); setShowExportMenu(false); }} className="block w-full text-left px-5 py-3 text-sm font-semibold text-gray-700 hover:bg-blue-50 transition-colors flex items-center gap-3">
                                            <div className="w-2.5 h-2.5 rounded-full bg-blue-500" /> {t('exportAllData')} (JSON)
                                        </button>
                                        <div className="border-t border-gray-100 my-1"></div>
                                        {[
                                            { key: 'customers', label: t('customers') },
                                            { key: 'orders',    label: t('orders') },
                                            { key: 'products',  label: t('products') },
                                            { key: 'bottles',   label: t('bottles') },
                                            { key: 'oils',      label: t('oils') },
                                            { key: 'shipping',  label: t('shipping') },
                                        ].map(item => (
                                            <button key={item.key} onClick={() => { exportCsv(data[item.key], item.key); setShowExportMenu(false); }} className="block w-full text-left px-5 py-2.5 text-sm text-gray-600 hover:bg-gray-50 transition-colors">
                                                {t('export')} {item.label} (CSV)
                                            </button>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>

                        {/* Import */}
                         <div className="relative group">
                            <label className="block text-xs font-bold text-gray-500 uppercase mb-3">{t('importData')}</label>
                            <Button onClick={() => { setShowImportMenu(!showImportMenu); setShowExportMenu(false); }} color="green" className="w-full justify-between h-12 px-5 rounded-xl border-2 border-transparent hover:border-green-200 shadow-sm">
                                <span className="flex items-center gap-3">
                                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="w-5 h-5">
                                        <path d={ICONS_MAP.upload} />
                                    </svg>
                                    {t('importData')}
                                </span>
                                <svg className={`w-4 h-4 transition-transform ${showImportMenu ? 'rotate-180' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" /></svg>
                            </Button>
                            {showImportMenu && (
                                <div className="absolute top-full left-0 mt-3 w-full bg-white border border-gray-200 rounded-2xl shadow-2xl z-20 overflow-hidden ring-1 ring-black ring-opacity-5">
                                    <div className="py-2">
                                        <button onClick={() => { handleFileImport('all'); setShowImportMenu(false); }} className="block w-full text-left px-5 py-3 text-sm font-semibold text-gray-700 hover:bg-green-50 transition-colors flex items-center gap-3">
                                            <div className="w-2.5 h-2.5 rounded-full bg-green-500" /> {t('importAllData')} (JSON)
                                        </button>
                                        <div className="border-t border-gray-100 my-1"></div>
                                        {[
                                            { key: 'customers', label: t('customers') },
                                            { key: 'orders',    label: t('orders') },
                                            { key: 'products',  label: t('products') },
                                            { key: 'bottles',   label: t('bottles') },
                                            { key: 'oils',      label: t('oils') },
                                            { key: 'shipping',  label: t('shipping') },
                                        ].map(item => (
                                            <button key={item.key} onClick={() => { handleFileImport(item.key); setShowImportMenu(false); }} className="block w-full text-left px-5 py-2.5 text-sm text-gray-600 hover:bg-gray-50 transition-colors">
                                                {t('import')} {item.label}
                                            </button>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                 </Card>
            </div>

            {/* Section: Team Management */}
            <SectionHeader title={t('teamManagement')} icon={<svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={ICONS_MAP.users} /></svg>} color="violet" />
            <Card className="space-y-8 p-6 lg:p-8">
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                    <div>
                        <h3 className="text-lg font-bold text-gray-900">{t('userAccounts') || 'User Accounts'}</h3>
                        <p className="text-sm text-gray-500">{t('userManagementDesc')}</p>
                    </div>
                    <div className="flex -space-x-2">
                         {users.slice(0, 5).map(u => (
                             <div key={u.id} className="w-10 h-10 rounded-full border-2 border-white bg-indigo-500 flex items-center justify-center text-white text-xs font-bold" title={u.email}>
                                 {u.email.charAt(0).toUpperCase()}
                             </div>
                         ))}
                         {users.length > 5 && (
                             <div className="w-10 h-10 rounded-full border-2 border-white bg-gray-200 flex items-center justify-center text-gray-600 text-xs font-bold">
                                 +{users.length - 5}
                             </div>
                         )}
                    </div>
                </div>

                {/* Add User */}
                <div className="bg-gray-50 rounded-2xl p-6 border border-gray-100">
                    <h4 className="font-bold text-gray-700 mb-4">{t('addUser')}</h4>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <input
                            type="email"
                            placeholder={t('userEmail')}
                            value={newUserEmail}
                            onChange={e => setNewUserEmail(e.target.value)}
                            className={`p-3 border rounded-xl bg-white ${FOCUSED_INPUT_STYLE}`}
                        />
                        <input
                            type="password"
                            placeholder={t('password')}
                            value={newUserPassword}
                            onChange={e => setNewUserPassword(e.target.value)}
                            className={`p-3 border rounded-xl bg-white ${FOCUSED_INPUT_STYLE}`}
                        />
                        <Button onClick={addUser} disabled={addingUser} color="green" className="h-[52px]">
                            {addingUser ? t('adding') : t('addUser')}
                        </Button>
                    </div>
                </div>

                {/* Users List */}
                {loadingUsers ? (
                    <div className="flex justify-center p-8">
                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
                    </div>
                ) : (
                    <div className="overflow-hidden border border-gray-100 rounded-2xl">
                        <table className="min-w-full divide-y divide-gray-200">
                            <thead className="bg-gray-50/50">
                                <tr>
                                    <th className="px-6 py-4 text-left text-xs font-bold text-gray-500 uppercase tracking-wider">{t('email')}</th>
                                    <th className="px-6 py-4 text-left text-xs font-bold text-gray-500 uppercase tracking-wider">{t('role')}</th>
                                    <th className="px-6 py-4 text-left text-xs font-bold text-gray-500 uppercase tracking-wider">{t('createdAt')}</th>
                                    <th className="px-6 py-4 text-right text-xs font-bold text-gray-500 uppercase tracking-wider">{t('actions')}</th>
                                </tr>
                            </thead>
                            <tbody className="bg-white divide-y divide-gray-100">
                                {users.map(user => (
                                    <tr key={user.id} className="hover:bg-gray-50/50 transition-colors">
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            <div className="flex items-center gap-3">
                                                <div className="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center text-gray-600 text-[10px] font-bold">
                                                    {user.email.charAt(0).toUpperCase()}
                                                </div>
                                                <span className="text-sm font-semibold text-gray-900">{user.email}</span>
                                            </div>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            {editingRole === user.id ? (
                                                <select
                                                    value={user.role || 'user'}
                                                    onChange={e => updateUserRole(user.id, e.target.value)}
                                                    className={`p-1.5 border rounded-lg text-sm ${FOCUSED_INPUT_STYLE}`}
                                                >
                                                    <option value="user">{t('user')}</option>
                                                    <option value="admin">{t('admin')}</option>
                                                </select>
                                            ) : (
                                                <Badge color={user.role === 'admin' ? 'bg-rose-100 text-rose-700 border border-rose-200' : 'bg-blue-100 text-blue-700 border border-blue-200'}>
                                                    {user.role === 'admin' ? t('admin') : t('user')}
                                                </Badge>
                                            )}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                            {new Date(user.created_at).toLocaleDateString()}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-right">
                                            <div className="flex justify-end gap-2">
                                                {editingRole === user.id ? (
                                                    <IconButton icon={ICONS_MAP.close} color="gray" onClick={() => setEditingRole(null)} tooltip={t('cancel')} size="sm" />
                                                ) : (
                                                    <IconButton icon={ICONS_MAP.edit} color="blue" onClick={() => setEditingRole(user.id)} tooltip={t('editRole')} size="sm" />
                                                )}
                                                <IconButton icon={ICONS_MAP.trash} color="red" onClick={() => removeUser(user.id, user.email)} tooltip={t('deleteUser')} size="sm" />
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </Card>

            <div className="mt-12 text-center text-gray-400 text-xs py-10 border-t border-gray-100">
                 <p>Lilac Valley CRM &copy; {new Date().getFullYear()} - {t('allRightsReserved') || 'All rights reserved.'}</p>
                 <p className="mt-1 opacity-50 font-mono">v2.4.0-stable</p>
            </div>

            {/* Modals - Kept at the end */}
            {showSupabaseModal && (
                <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex justify-center items-center z-50 p-4 animate-in fade-in duration-300">
                    <div className="bg-white rounded-3xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col scale-in duration-300">
                        <div className="flex justify-between items-center p-6 border-b bg-gray-50">
                            <h2 className="text-2xl font-bold text-gray-800">{t('databaseConnection')}</h2>
                            <IconButton icon={ICONS_MAP.close} color="gray" onClick={() => setShowSupabaseModal(false)} tooltip="Close" />
                        </div>
                        <div className="p-8 space-y-8 overflow-y-auto">
                            <p className="text-gray-600">{t('supabaseConfigDesc') || 'Configure your Supabase backend connection and security keys.'}</p>

                            <div className="bg-amber-50 border border-amber-200 rounded-2xl p-5 flex gap-4">
                                <div className="flex-shrink-0 text-amber-500 mt-1">
                                    <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.93 5h13.86c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                                    </svg>
                                </div>
                                <div>
                                    <h3 className="text-sm font-bold text-amber-800">{t('securityWarning')}</h3>
                                    <p className="mt-1 text-sm text-amber-700 leading-relaxed">{t('securityWarningDesc')}</p>
                                </div>
                            </div>

                            <div className="grid grid-cols-1 gap-6">
                                <div className="space-y-2">
                                    <label className="block text-sm font-bold text-gray-700 uppercase tracking-wider">Supabase URL</label>
                                    <input
                                        type="text"
                                        value={supabaseUrl}
                                        onChange={e => setSupabaseUrl(e.target.value)}
                                        className={`block w-full p-3 border rounded-xl ${FOCUSED_INPUT_STYLE}`}
                                        placeholder="https://your-project.supabase.co"
                                    />
                                </div>
                                <div className="space-y-2">
                                    <label className="block text-sm font-bold text-gray-700 uppercase tracking-wider">Anon Key</label>
                                    <div className="flex gap-2">
                                        <input
                                            type={showKey ? "text" : "password"}
                                            value={supabaseKey}
                                            onChange={e => setSupabaseKey(e.target.value)}
                                            className={`block w-full p-3 border rounded-xl font-mono text-sm ${FOCUSED_INPUT_STYLE}`}
                                            placeholder="eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9..."
                                        />
                                        <Button onClick={() => setShowKey(!showKey)} color="gray" className="rounded-xl">
                                            {showKey ? (t('hide') || 'Hide') : (t('show') || 'Show')}
                                        </Button>
                                    </div>
                                </div>
                            </div>

                            <div className="flex justify-end gap-3 pt-6 border-t mt-4">
                                <Button onClick={() => setShowSupabaseModal(false)} color="gray" className="rounded-xl px-6">{t('cancel')}</Button>
                                <Button onClick={saveSupabaseConfig} color="green" className="rounded-xl px-8 shadow-md">
                                    {t('saveConfiguration')}
                                </Button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Structure Configuration Modal */}
            {showStructureModal && (
                <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex justify-center items-center z-50 p-4 animate-in fade-in duration-300">
                    <div className="bg-white rounded-3xl shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col scale-in duration-300">
                        <div className="flex justify-between items-center p-6 border-b bg-gray-50">
                            <div>
                                <h2 className="text-2xl font-bold text-gray-800">{t('storeStructure')}</h2>
                                <p className="text-sm text-gray-500 mt-1">{t('storeStructureDesc') || 'Define the fundamental categories and sizes for your product inventory.'}</p>
                            </div>
                            <IconButton icon={ICONS_MAP.close} color="gray" onClick={() => setShowStructureModal(false)} tooltip="Close" />
                        </div>
                        <div className="p-8 space-y-10 overflow-y-auto">
                            
                            {/* Bottle Types Configuration */}
                            <div className="space-y-6">
                                <div className="flex items-center gap-3">
                                    <div className={`p-2 rounded-lg bg-${data?.settings?.primaryColor || 'indigo'}-50 text-${data?.settings?.primaryColor || 'indigo'}-600`}>
                                        <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" /></svg>
                                    </div>
                                    <h3 className="text-xl font-bold text-gray-800">{t('bottleTypes')}</h3>
                                </div>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                    {data.stockSettings.bottleVariants && Object.entries(data.stockSettings.bottleVariants).map(([gender, sizes]) => (
                                        <div key={gender} className="bg-gray-50 rounded-2xl p-5 border border-gray-100 transition-all hover:shadow-sm">
                                            <div className="flex justify-between items-center mb-4">
                                                {renamingCategory.type === 'bottle' && renamingCategory.oldName === gender ? (
                                                    <div className="flex items-center gap-2">
                                                        <input
                                                            type="text"
                                                            value={renamingCategory.currentName}
                                                            onChange={e => setRenamingCategory(prev => ({ ...prev, currentName: e.target.value }))}
                                                            onBlur={() => {
                                                                if (renamingCategory.currentName.trim() && renamingCategory.currentName !== renamingCategory.oldName) {
                                                                    const newVariants = { ...data.stockSettings.bottleVariants };
                                                                    newVariants[renamingCategory.currentName] = newVariants[renamingCategory.oldName];
                                                                    delete newVariants[renamingCategory.oldName];
                                                                    setData(prev => ({
                                                                        ...prev,
                                                                        stockSettings: { ...prev.stockSettings, bottleVariants: newVariants }
                                                                    }));
                                                                }
                                                                setRenamingCategory({ type: null, oldName: '', currentName: '' });
                                                            }}
                                                            onKeyDown={e => {
                                                                if (e.key === 'Enter') e.target.blur();
                                                                if (e.key === 'Escape') setRenamingCategory({ type: null, oldName: '', currentName: '' });
                                                            }}
                                                            autoFocus
                                                            className={`p-1 text-sm border-b font-bold border-gray-300 focus:border-indigo-500 outline-none w-32`}
                                                        />
                                                    </div>
                                                ) : (
                                                    <h4 className="font-bold text-gray-700 capitalize flex items-center gap-2 group">
                                                        <span className={`w-2 h-2 rounded-full bg-${data?.settings?.primaryColor || 'indigo'}-500`}></span>
                                                        {gender}
                                                        <button 
                                                            onClick={() => setRenamingCategory({ type: 'bottle', oldName: gender, currentName: gender })}
                                                            className="opacity-0 group-hover:opacity-100 p-1 rounded-md hover:bg-gray-200 transition-all ml-1"
                                                        >
                                                            <svg className="w-3.5 h-3.5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={ICONS_MAP.edit} /></svg>
                                                        </button>
                                                    </h4>
                                                )}
                                                <Badge color="gray">{sizes.length} {t('sizes') || 'Sizes'}</Badge>
                                            </div>
                                            <div className="flex flex-wrap gap-2 min-h-[40px]">
                                                {sizes.map(size => (
                                                    <div key={size} className="group bg-white border border-gray-200 pl-3 pr-1 py-1 rounded-full text-sm font-medium text-gray-700 flex items-center gap-1 shadow-sm hover:border-red-200 hover:text-red-700 transition-colors">
                                                        {size}
                                                        <button 
                                                            onClick={() => {
                                                                const newVariants = { ...data.stockSettings.bottleVariants };
                                                                newVariants[gender] = newVariants[gender].filter(s => s !== size);
                                                                if (newVariants[gender].length === 0) delete newVariants[gender];
                                                                setData(prev => ({
                                                                    ...prev,
                                                                    stockSettings: { ...prev.stockSettings, bottleVariants: newVariants }
                                                                }));
                                                            }}
                                                            className="p-1 rounded-full opacity-40 group-hover:opacity-100 hover:bg-red-50"
                                                        >
                                                            <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M6 18L18 6M6 6l12 12" /></svg>
                                                        </button>
                                                    </div>
                                                ))}
                                            </div>
                                            <div className="mt-5 flex gap-2">
                                                <input
                                                    type="text"
                                                    placeholder={t('addNewSize') || "Add size (e.g. 50ml)"}
                                                    className={`flex-1 p-2.5 text-sm border rounded-xl bg-white shadow-inner ${FOCUSED_INPUT_STYLE}`}
                                                    onKeyPress={(e) => {
                                                        if (e.key === 'Enter' && e.target.value.trim()) {
                                                            const newSize = e.target.value.trim();
                                                            const newVariants = { ...data.stockSettings.bottleVariants };
                                                            if (!newVariants[gender]) newVariants[gender] = [];
                                                            if (!newVariants[gender].includes(newSize)) {
                                                                newVariants[gender].push(newSize);
                                                                setData(prev => ({
                                                                    ...prev,
                                                                    stockSettings: { ...prev.stockSettings, bottleVariants: newVariants }
                                                                }));
                                                                e.target.value = '';
                                                            }
                                                        }
                                                    }}
                                                />
                                            </div>
                                        </div>
                                    ))}

                                    {/* Add New Bottle Category Card */}
                                    <div className="bg-indigo-50/30 border border-dashed border-indigo-200 rounded-2xl p-5 flex flex-col justify-center items-center text-center">
                                        <div className="p-2 rounded-full bg-indigo-100 text-indigo-600 mb-2">
                                            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" /></svg>
                                        </div>
                                        <h4 className="font-bold text-gray-700 mb-1">{t('newBottleCategory')}</h4>
                                        <p className="text-xs text-gray-500 mb-4">{t('newBottleCategoryHelp')}</p>
                                        <div className="flex gap-2 w-full">
                                            <input
                                                type="text"
                                                placeholder={t('categoryNamePlaceholder') || "e.g. Unisex"}
                                                className={`flex-1 p-2.5 text-sm border rounded-xl bg-white ${FOCUSED_INPUT_STYLE}`}
                                                id="newBottleCategoryInput"
                                            />
                                            <Button
                                                color="indigo"
                                                onClick={() => {
                                                    const input = document.getElementById('newBottleCategoryInput');
                                                    if (input.value.trim()) {
                                                        const newCategory = input.value.trim().toLowerCase();
                                                        const newVariants = { ...data.stockSettings.bottleVariants };
                                                        if (!newVariants[newCategory]) {
                                                            newVariants[newCategory] = [];
                                                            setData(prev => ({
                                                                ...prev,
                                                                stockSettings: { ...prev.stockSettings, bottleVariants: newVariants }
                                                            }));
                                                            input.value = '';
                                                        }
                                                    }
                                                }}
                                                className="rounded-xl"
                                                size="sm"
                                            >
                                                {t('add')}
                                            </Button>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Oil Categories Configuration */}
                            <div className="space-y-6 pt-6 border-t">
                                <div className="flex items-center gap-3">
                                    <div className={`p-2 rounded-lg bg-orange-50 text-orange-600`}>
                                        <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19.428 15.428a2 2 0 00-1.022-.547l-2.387-.477a6 6 0 00-3.86.517l-.605.282a5.998 5.998 0 01-3.351.455l-2.387-.477a2 2 0 00-1.928 1.488l-.758 3.551a2 2 0 001.954 2.215h13.235a2 2 0 001.954-2.215l-.758-3.551zM5 8a3 3 0 116 0 3 3 0 01-6 0zM15 8a3 3 0 116 0 3 3 0 01-6 0z" /></svg>
                                    </div>
                                    <h3 className="text-xl font-bold text-gray-800">{t('oilCategories')}</h3>
                                </div>
                                <div className="flex flex-wrap gap-3">
                                    {data.stockSettings.oilCategories && data.stockSettings.oilCategories.map(category => (
                                        <div key={category} className="group bg-white border border-gray-200 pl-4 pr-1 py-1 rounded-2xl text-sm font-bold text-gray-700 flex items-center gap-2 shadow-md hover:border-orange-200 transition-all">
                                            {renamingCategory.type === 'oil' && renamingCategory.oldName === category ? (
                                                <input
                                                    type="text"
                                                    value={renamingCategory.currentName}
                                                    onChange={e => setRenamingCategory(prev => ({ ...prev, currentName: e.target.value }))}
                                                    onBlur={() => {
                                                        if (renamingCategory.currentName.trim() && renamingCategory.currentName !== renamingCategory.oldName) {
                                                            const newCategories = data.stockSettings.oilCategories.map(c => 
                                                                c === renamingCategory.oldName ? renamingCategory.currentName : c
                                                            );
                                                            setData(prev => ({ 
                                                                ...prev, 
                                                                stockSettings: { ...prev.stockSettings, oilCategories: newCategories } 
                                                            }));
                                                        }
                                                        setRenamingCategory({ type: null, oldName: '', currentName: '' });
                                                    }}
                                                    onKeyDown={e => {
                                                        if (e.key === 'Enter') e.target.blur();
                                                        if (e.key === 'Escape') setRenamingCategory({ type: null, oldName: '', currentName: '' });
                                                    }}
                                                    autoFocus
                                                    className="p-1 border-b border-orange-300 outline-none w-24 text-sm"
                                                />
                                            ) : (
                                                <div className="flex items-center gap-2 py-1">
                                                    {category}
                                                    <button 
                                                        onClick={() => setRenamingCategory({ type: 'oil', oldName: category, currentName: category })}
                                                        className="opacity-0 group-hover:opacity-100 p-1 rounded-md hover:bg-gray-100 transition-all"
                                                    >
                                                        <svg className="w-3.5 h-3.5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={ICONS_MAP.edit} /></svg>
                                                    </button>
                                                </div>
                                            )}
                                            <IconButton
                                                icon={ICONS_MAP.trash}
                                                color="red"
                                                size="xs"
                                                onClick={() => {
                                                    const newCategories = data.stockSettings.oilCategories.filter(c => c !== category);
                                                    setData(prev => ({ ...prev, stockSettings: { ...prev.stockSettings, oilCategories: newCategories } }));
                                                }}
                                                className="opacity-40 group-hover:opacity-100"
                                                tooltip="Remove category"
                                            />
                                        </div>
                                    ))}
                                    <div className="flex gap-2 ml-auto">
                                        <input
                                            type="text"
                                            placeholder={t('newOilCategoryPlaceholder') || "New category..."}
                                            className={`p-2.5 text-sm border rounded-xl bg-white w-48 ${FOCUSED_INPUT_STYLE}`}
                                            id="newOilCategoryInput"
                                        />
                                        <Button
                                            color="orange"
                                            onClick={() => {
                                                const input = document.getElementById('newOilCategoryInput');
                                                if (input.value.trim()) {
                                                    const newCategory = input.value.trim();
                                                    const newCategories = [...(data.stockSettings.oilCategories || [])];
                                                    if (!newCategories.includes(newCategory)) {
                                                        newCategories.push(newCategory);
                                                        setData(prev => ({ ...prev, stockSettings: { ...prev.stockSettings, oilCategories: newCategories } }));
                                                        input.value = '';
                                                    }
                                                }
                                            }}
                                            className="rounded-xl"
                                        >
                                            {t('add')}
                                        </Button>
                                    </div>
                                </div>
                            </div>

                            <div className="flex justify-end gap-3 pt-6 border-t">
                                <Button onClick={() => setShowStructureModal(false)} color="gray" className="rounded-xl px-10 border-gray-200">{t('close')}</Button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* WooCommerce Configuration Modal */}
            {showWooCommerceModal && (
                <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex justify-center items-center z-50 p-4 animate-in fade-in duration-300">
                    <div className="bg-white rounded-3xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col scale-in duration-300">
                        <div className="flex justify-between items-center p-6 border-b bg-gray-50">
                            <h2 className="text-2xl font-bold text-gray-800">{t('wooCommerceIntegration')}</h2>
                            <IconButton icon={ICONS_MAP.close} color="gray" onClick={() => setShowWooCommerceModal(false)} tooltip="Close" />
                        </div>
                        <div className="p-8 space-y-8 overflow-y-auto">
                            <p className="text-gray-600">{t('wooIntegrationDesc') || 'Connect your WooCommerce store to automatically import orders into CRM.'}</p>

                            <div className="bg-indigo-50 border border-indigo-100 rounded-2xl p-6">
                                <h3 className="text-sm font-bold text-indigo-800 mb-3 uppercase tracking-wider">{t('howToConnect') || 'How to Connect'}:</h3>
                                <ol className="list-decimal pl-5 text-sm text-indigo-700 space-y-2 leading-relaxed">
                                    <li>{t('wooStep1') || 'Add the provided PHP snippet to your WordPress site.'}</li>
                                    <li>{t('wooStep2') || 'Go to WooCommerce > Settings > Advanced > Webhooks.'}</li>
                                    <li>{t('wooStep3') || 'Create a new webhook for "Order created".'}</li>
                                    <li>{t('wooStep4') || 'Use the Delivery URL below.'}</li>
                                </ol>
                            </div>

                            <div className="grid grid-cols-1 gap-6">
                                <div className="space-y-2">
                                    <label className="block text-sm font-bold text-gray-700 mb-1">{t('webhookUrl') || 'Webhook Delivery URL'}</label>
                                    <div className="flex gap-2">
                                        <input
                                            type="text"
                                            readOnly
                                            value={supabaseUrl ? `${supabaseUrl}/rest/v1/pending_woo_orders` : 'Configure Supabase URL first'}
                                            className={`flex-1 p-3 border rounded-xl bg-gray-50 text-sm font-mono text-gray-600 border-gray-200 outline-none`}
                                        />
                                        <Button
                                            onClick={() => {
                                                navigator.clipboard.writeText(`${supabaseUrl}/rest/v1/pending_woo_orders`);
                                                showAlert(t('urlCopied') || 'URL copied to clipboard');
                                            }}
                                            color="gray"
                                            className="rounded-xl"
                                            disabled={!supabaseUrl}
                                        >
                                            {t('copy')}
                                        </Button>
                                    </div>
                                    <p className="text-xs text-gray-400 italic mt-1">{t('webhookHelp') || "Paste this into the WooCommerce Webhook 'Delivery URL' field."}</p>
                                </div>

                                <div className="space-y-2">
                                    <label className="block text-sm font-bold text-gray-700 mb-1">{t('supabaseAnonKey') || 'Supabase Anon Key'}</label>
                                    <div className="flex gap-2">
                                        <input
                                            type="password"
                                            readOnly
                                            value={supabaseKey || 'Configure Supabase key first'}
                                            className={`flex-1 p-3 border rounded-xl bg-gray-50 text-sm font-mono text-gray-600 border-gray-200 outline-none`}
                                        />
                                        <Button
                                            onClick={() => {
                                                navigator.clipboard.writeText(supabaseKey);
                                                showAlert(t('keyCopied') || 'Key copied to clipboard');
                                            }}
                                            color="gray"
                                            className="rounded-xl"
                                            disabled={!supabaseKey}
                                        >
                                            {t('copy')}
                                        </Button>
                                    </div>
                                    <p className="text-xs text-gray-400 italic mt-1">{t('keyHelp') || 'You must paste this key into the PHP snippet on your WordPress site.'}</p>
                                </div>
                            </div>

                            <div className="flex justify-end gap-3 pt-6 border-t mt-4">
                                <Button onClick={() => setShowWooCommerceModal(false)} color="gray" className="rounded-xl px-10 border-gray-200">{t('close')}</Button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* WooCommerce Mapping Modal */}
            {showWooMappingModal && (
                <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex justify-center items-center z-50 p-4 animate-in fade-in duration-300">
                    <div className="bg-white rounded-3xl shadow-2xl w-full max-w-4xl max-h-[90vh] flex flex-col scale-in duration-300 overflow-hidden">
                        <div className={`flex justify-between items-center p-6 border-b bg-${data?.settings?.primaryColor || 'indigo'}-600 text-white`}>
                            <div>
                                <h2 className="text-2xl font-bold">{t('wooMappings')}</h2>
                                <p className="text-indigo-100 text-sm mt-1">{t('wooMappingsDesc')}</p>
                            </div>
                            <IconButton icon={ICONS_MAP.close} color="white" onClick={() => setShowWooMappingModal(false)} tooltip="Close" className="hover:bg-white/10" />
                        </div>
                        <div className="p-8 flex-1 overflow-y-auto space-y-8">
                            <div className="flex flex-col md:flex-row justify-between items-start gap-4">
                                <div className="max-w-2xl">
                                    <p className="text-gray-600 leading-relaxed font-medium">
                                        {t('mappingInstruction') || 'Map your complex WooCommerce item names (like "Oud Collet - Gender: For Him, Size: 50ml") to exactly match your CRM product names.'}
                                    </p>
                                </div>
                                <Button onClick={handleWooCsvImport} color="indigo" className="whitespace-nowrap flex-shrink-0 shadow-lg px-6 py-3 rounded-2xl">
                                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="w-5 h-5 mr-2"><path d={ICONS_MAP.upload} /></svg>
                                    {t('bulkAutoMap')}
                                </Button>
                            </div>

                            <div className="bg-orange-50/50 p-6 rounded-2xl border border-orange-100 shadow-sm ring-1 ring-orange-200/50">
                                <h3 className="font-bold text-orange-900 border-b border-orange-100 pb-3 mb-5 flex items-center gap-2">
                                    <span className="w-2 h-2 rounded-full bg-orange-500"></span>
                                    {t('addNewMapping')}
                                </h3>
                                <div className="flex flex-col md:flex-row gap-5 items-end">
                                    <div className="flex-[2] w-full">
                                        <label className="block text-xs font-bold text-orange-800 uppercase mb-2 tracking-wider">{t('wooItemName')}</label>
                                        <input
                                            type="text"
                                            placeholder="e.g. Lavender - Size: 50ml"
                                            value={newWooMappingName}
                                            onChange={e => setNewWooMappingName(e.target.value)}
                                            className={`w-full p-3.5 border border-orange-200 rounded-xl bg-white focus:ring-4 focus:ring-orange-100 transition-all ${FOCUSED_INPUT_STYLE}`}
                                        />
                                    </div>
                                    <div className="flex-[2] w-full">
                                        <label className="block text-xs font-bold text-orange-800 uppercase mb-2 tracking-wider">{t('mapsToCrmProduct')}</label>
                                        <select
                                            value={newWooMappingCrmItem}
                                            onChange={e => setNewWooMappingCrmItem(e.target.value)}
                                            className={`w-full p-3.5 border border-orange-200 rounded-xl bg-white focus:ring-4 focus:ring-orange-100 transition-all ${FOCUSED_INPUT_STYLE}`}
                                        >
                                            <option value="">{t('selectProduct')}</option>
                                            {data.products.sort((a, b) => a.product.localeCompare(b.product)).map(p => (
                                                <option key={p.product} value={p.product}>{p.product}</option>
                                            ))}
                                        </select>
                                    </div>
                                    <div className="flex-1 w-full">
                                        <Button onClick={handleSaveWooMapping} color="orange" className="w-full h-[52px] rounded-xl shadow-md transform transition-all active:scale-95">
                                            {t('saveMapping')}
                                        </Button>
                                    </div>
                                </div>
                            </div>

                            <div className="border border-gray-100 rounded-3xl overflow-hidden shadow-xl ring-1 ring-black/5 bg-white">
                                <table className="min-w-full divide-y divide-gray-100">
                                    <thead className="bg-gray-50/50">
                                        <tr>
                                            <th className="px-8 py-5 text-left text-xs font-bold text-gray-500 uppercase tracking-widest">{t('wooItemName')}</th>
                                            <th className="px-8 py-5 text-left text-xs font-bold text-gray-500 uppercase tracking-widest">{t('crmProduct')}</th>
                                            <th className="px-8 py-5 text-right text-xs font-bold text-gray-500 uppercase tracking-widest">{t('actions')}</th>
                                        </tr>
                                    </thead>
                                    <tbody className="bg-white divide-y divide-gray-100">
                                        {wooMappings.length === 0 ? (
                                            <tr><td colSpan="3" className="px-8 py-12 text-center text-gray-400 italic">{t('noMappings')}</td></tr>
                                        ) : (
                                            wooMappings.map(map => (
                                                <tr key={map.woo_product_name} className="hover:bg-indigo-50/20 transition-colors group">
                                                    <td className="px-8 py-4 whitespace-nowrap text-sm text-gray-900 font-bold">{map.woo_product_name}</td>
                                                    <td className="px-8 py-4 whitespace-nowrap">
                                                       <Badge color={`bg-${data?.settings?.primaryColor || 'indigo'}-50 text-${data?.settings?.primaryColor || 'indigo'}-700 border-${data?.settings?.primaryColor || 'indigo'}-200`} className="font-extrabold px-3 py-1">
                                                           {map.crm_product_name}
                                                       </Badge>
                                                    </td>
                                                    <td className="px-8 py-4 whitespace-nowrap text-right text-sm">
                                                        <IconButton icon={ICONS_MAP.trash} color="red" onClick={() => handleDeleteWooMapping(map.woo_product_name)} tooltip="Delete Mapping" className="opacity-0 group-hover:opacity-100" />
                                                    </td>
                                                </tr>
                                            ))
                                        )}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Invoice Settings Modal */}
            {showInvoiceSettingsModal && (
                <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex justify-center items-center z-50 p-4 animate-in fade-in duration-300">
                    <div className="bg-white rounded-3xl shadow-2xl w-full max-w-4xl max-h-[90vh] flex flex-col scale-in duration-300 overflow-hidden">
                        <div className={`flex justify-between items-center p-6 border-b bg-${data?.settings?.primaryColor || 'indigo'}-600 text-white`}>
                            <div>
                                <h2 className="text-2xl font-bold">{t('invoiceSettings')}</h2>
                                <p className="text-indigo-100 text-sm mt-1">{t('invoiceSettingsDesc')}</p>
                            </div>
                            <IconButton icon={ICONS_MAP.close} color="white" onClick={() => setShowInvoiceSettingsModal(false)} tooltip="Close" className="hover:bg-white/10" />
                        </div>
                        <div className="p-10 flex-1 overflow-y-auto space-y-10">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
                                <div className="space-y-6">
                                    <div className="space-y-4">
                                        <label className="block text-sm font-bold text-gray-700 uppercase tracking-widest">{t('invoiceLogo')}</label>
                                        <div className="flex flex-col gap-4">
                                            <div className="w-full aspect-video bg-gray-50 rounded-2xl border-2 border-dashed border-gray-200 flex items-center justify-center overflow-hidden group relative transition-all hover:border-indigo-300">
                                                {data.generalSettings?.invoiceSettings?.logoUrl ? (
                                                    <img
                                                        src={data.generalSettings.invoiceSettings.logoUrl}
                                                        alt="Invoice Logo"
                                                        className="max-w-[80%] max-h-[80%] object-contain"
                                                        onError={e => e.target.style.display = 'none'}
                                                    />
                                                ) : (
                                                    <div className="text-center">
                                                        <svg className="w-12 h-12 text-gray-300 mx-auto" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" /></svg>
                                                        <p className="text-xs text-gray-400 mt-2 font-medium">{t('noLogoSelected')}</p>
                                                    </div>
                                                )}
                                            </div>
                                            <div className="flex gap-2">
                                                <label className={`cursor-pointer bg-${data?.settings?.primaryColor || 'indigo'}-50 border border-${data?.settings?.primaryColor || 'indigo'}-200 text-${data?.settings?.primaryColor || 'indigo'}-700 px-4 py-3 rounded-xl text-sm font-bold hover:bg-${data?.settings?.primaryColor || 'indigo'}-100 transition-all shadow-sm flex items-center justify-center h-[52px]`}>
                                                    <svg className="w-4 h-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" /></svg>
                                                    {t('uploadLogo')}
                                                    <input type="file" accept="image/*" className="hidden" onChange={async (e) => {
                                                        const file = e.target.files[0];
                                                        if (!file) return;
                                                        try {
                                                            const { supabase: sb } = await import('../lib/api/supabase');
                                                            const ext = file.name.split('.').pop();
                                                            const path = `logo-${Date.now()}.${ext}`;
                                                            const { error } = await sb.storage.from('Pictures').upload(path, file, { upsert: true });
                                                            if (error) throw error;
                                                            const projectUrl = sb.supabaseUrl;
                                                            const url = `${projectUrl}/storage/v1/object/public/Pictures/${encodeURIComponent(path)}`;
                                                            setData(prev => ({ ...prev, generalSettings: { ...prev.generalSettings, invoiceSettings: { ...(prev.generalSettings.invoiceSettings || {}), logoUrl: url } } }));
                                                        } catch (err) { alert('Upload failed: ' + err.message); }
                                                    }} />
                                                </label>
                                                <input
                                                    type="text"
                                                    value={data.generalSettings?.invoiceSettings?.logoUrl || ''}
                                                    onChange={e => setData(prev => ({ ...prev, generalSettings: { ...prev.generalSettings, invoiceSettings: { ...(prev.generalSettings.invoiceSettings || {}), logoUrl: e.target.value } } }))}
                                                    className={`flex-1 p-3 border rounded-xl text-sm bg-gray-50/50 ${FOCUSED_INPUT_STYLE}`}
                                                    placeholder="https://..."
                                                />
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                <div className="space-y-8">
                                    <div className="space-y-4">
                                        <label className="block text-sm font-bold text-gray-700 uppercase tracking-widest">{t('visualStyle')}</label>
                                        <div className="grid grid-cols-1 gap-6">
                                            <div className="space-y-2">
                                                <label className="text-xs font-bold text-gray-400 uppercase tracking-wider">{t('accentColor')}</label>
                                                <div className="flex gap-3 items-center">
                                                    <div className="relative group">
                                                        <input
                                                            type="color"
                                                            value={data.generalSettings?.invoiceSettings?.primaryColor || '#7c3aed'}
                                                            onChange={e => setData(prev => ({ ...prev, generalSettings: { ...prev.generalSettings, invoiceSettings: { ...(prev.generalSettings.invoiceSettings || {}), primaryColor: e.target.value } } }))}
                                                            className="h-12 w-20 border-2 border-white rounded-xl cursor-pointer shadow-md ring-1 ring-gray-200 overflow-hidden"
                                                        />
                                                    </div>
                                                    <input
                                                        type="text"
                                                        value={data.generalSettings?.invoiceSettings?.primaryColor || '#7c3aed'}
                                                        onChange={e => setData(prev => ({ ...prev, generalSettings: { ...prev.generalSettings, invoiceSettings: { ...(prev.generalSettings.invoiceSettings || {}), primaryColor: e.target.value } } }))}
                                                        className={`flex-1 p-3 border rounded-xl text-sm font-mono bg-white shadow-inner ${FOCUSED_INPUT_STYLE}`}
                                                        placeholder="#7c3aed"
                                                    />
                                                </div>
                                            </div>

                                            <div className="space-y-4 pt-4 border-t border-gray-100">
                                                <label className="text-xs font-bold text-gray-400 uppercase tracking-wider">{t('displayPreferences')}</label>
                                                <div className="space-y-3">
                                                    {[
                                                        { id: 'invoiceShowEmail',       checked: data.generalSettings?.invoiceSettings?.showEmail !== false,       label: t('showEmailOnInvoice'),       key: 'showEmail' },
                                                        { id: 'invoiceShowCompanyName', checked: data.generalSettings?.invoiceSettings?.showCompanyName !== false, label: t('showCompanyNameOnInvoice'), key: 'showCompanyName' },
                                                    ].map(opt => (
                                                        <label key={opt.id} className="flex items-center gap-4 p-4 rounded-xl border border-gray-100 hover:bg-gray-50 transition-all cursor-pointer group">
                                                            <div className="relative">
                                                                <input
                                                                    type="checkbox"
                                                                    id={opt.id}
                                                                    checked={opt.checked}
                                                                    onChange={e => setData(prev => ({ ...prev, generalSettings: { ...prev.generalSettings, invoiceSettings: { ...(prev.generalSettings.invoiceSettings || {}), [opt.key]: e.target.checked } } }))}
                                                                    className={`h-6 w-6 rounded-lg text-indigo-600 focus:ring-indigo-500 border-gray-300 transition-all cursor-pointer`}
                                                                />
                                                            </div>
                                                            <span className="text-sm font-bold text-gray-700 group-hover:text-indigo-700 transition-colors">{opt.label}</span>
                                                        </label>
                                                    ))}
                                                </div>
                                            </div>

                                            <div className="space-y-2 pt-4 border-t border-gray-100">
                                                <label className="text-xs font-bold text-gray-400 uppercase tracking-wider">{t('footerText')}</label>
                                                <input
                                                    type="text"
                                                    value={data.generalSettings?.invoiceSettings?.footerText || ''}
                                                    onChange={e => setData(prev => ({ ...prev, generalSettings: { ...prev.generalSettings, invoiceSettings: { ...(prev.generalSettings.invoiceSettings || {}), footerText: e.target.value } } }))}
                                                    className={`block w-full p-4 border rounded-xl text-sm bg-white shadow-inner focus:ring-4 focus:ring-indigo-50 transition-all ${FOCUSED_INPUT_STYLE}`}
                                                    placeholder={t('footerPlaceholder') || "Thank you for your purchase!"}
                                                />
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div className="flex justify-end gap-3 pt-8 border-t">
                                <Button onClick={() => setShowInvoiceSettingsModal(false)} color="gray" className="rounded-xl px-12 border-gray-200 font-bold">{t('close')}</Button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

        </div>
    );
}